import { useState, useCallback, useRef } from 'react';
import { SecondaryButtonMedium } from '@/components/base/Buttons';
import { TypographyText, TypographyH6 } from '@/components/base/Typography';
import { PrimaryInput } from '@/components/form/Inputs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from '@/components/ui/dialog';

interface NewsletterDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// PrimaryInput originates from a JS module, so we cast it to any to appease TS
const InputAny = PrimaryInput as any;

export const NewsletterDialog = ({ open, onOpenChange }: NewsletterDialogProps) => {
  const [hasSeen, setHasSeen] = useState<boolean>(() => {
    if (typeof document !== 'undefined') {
      return document.cookie.includes('newsletter_seen=true');
    }
    return false;
  });

  // Delay logic: wait ~3s after `open` is triggered before actually showing the dialog
  const [delayPassed, setDelayPassed] = useState<boolean>(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  if (open && !hasSeen && !delayPassed && !timerRef.current) {
    timerRef.current = setTimeout(() => {
      setDelayPassed(true);
    }, 3000);
  }

  const markNewsletterSeen = useCallback(() => {
    try {
      if (typeof document !== 'undefined') {
        // Persist the fact that the newsletter dialog has been shown/closed for one year
        document.cookie = 'newsletter_seen=true; path=/; max-age=31536000';
      }
    } catch (err) {
      // Fail silently – cookies might be disabled
    }
  }, []);

  const [email, setEmail] = useState<string>('');
  const [status, setStatus] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!email) return;

    try {
      setLoading(true);

      const res = await fetch('/api/klaviyo', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, createDiscount: true }),
      });

      const json = await res.json();

      if (res.ok) {
        setStatus('Thank you for subscribing!');
        setEmail('');

        // Mark as seen so the dialog does not re-appear on future visits
        markNewsletterSeen();
        setHasSeen(true);

        if (json?.discountCode) {
          try {
            localStorage.setItem('signup_discount', json.discountCode as string);
          } catch (err) {
            // localStorage might be unavailable (SSR / privacy settings)
          }
        }
      } else {
        setStatus(json?.error || 'Something went wrong, please try again.');
      }
    } catch (error) {
      setStatus('Something went wrong, please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDialogOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      markNewsletterSeen();
      setHasSeen(true);
    }
    onOpenChange(isOpen);
  };

  // Only display the dialog if it is requested by the parent, the user hasn't seen it yet, and the delay has passed
  const actualOpen = open && !hasSeen && delayPassed;

  return (
    <Dialog open={actualOpen} onOpenChange={handleDialogOpenChange}>
      <DialogContent
        className="top-auto bottom-0 left-0 flex h-[50vh] w-screen max-w-none translate-x-0 translate-y-0 flex-col justify-center rounded-t-lg p-4 sm:top-[50%] sm:bottom-auto sm:left-[50%] sm:h-auto sm:max-w-lg sm:translate-x-[-50%] sm:translate-y-[-50%] sm:space-y-4 sm:rounded-lg sm:p-6"
        showCloseButton={false}
      >
        <div className="space-y-4">
          <DialogHeader>
            <DialogTitle>
              <TypographyH6 className="">Stay in the loop</TypographyH6>
            </DialogTitle>
            <DialogDescription>
              <TypographyText className="text-muted-foreground">
                Sign up for email and take $100 off your next purchase.
              </TypographyText>
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="flex flex-col space-y-4">
            <InputAny
              type="email"
              required
              placeholder="Enter your email"
              value={email}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
              className="w-full"
            />
            <SecondaryButtonMedium className="w-full" type="submit" disabled={loading}>
              {loading ? 'Submitting...' : 'Subscribe'}
            </SecondaryButtonMedium>
          </form>

          // Link to dismiss the dialog without subscribing
          <DialogClose>
            <TypographyText className="text-muted-foreground cursor-pointer text-center underline">
              No thanks, I'll pay the full price
            </TypographyText>
          </DialogClose>

          {status && (
            <TypographyText className="text-muted-foreground text-center">{status}</TypographyText>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
